import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const PostImageCropperModal = ({ open, onClose, imageSrc, onCrop, onSkip }) => {
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    // For post images, use 80% of image dimensions maintaining aspect ratio
    const cropWidth = width * 0.8;
    const cropHeight = height * 0.8;
    
    const newCrop = {
      unit: "px",
      width: cropWidth,
      height: cropHeight,
      x: (width - cropWidth) / 2,
      y: (height - cropHeight) / 2,
    };
    
    setCrop(newCrop);
    setCompletedCrop(newCrop);
  };

  useEffect(() => {
    if (open) {
      setCrop(undefined);
      setCompletedCrop(null);
    }
  }, [open]);

  const getCroppedImg = (image, crop) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const cropWidth = crop.width * scaleX;
    const cropHeight = crop.height * scaleY;

    canvas.width = cropWidth;
    canvas.height = cropHeight;

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error("Canvas is empty");
          return;
        }
        const file = new File([blob], "cropped-image.jpg", {
          type: "image/jpeg",
        });
        resolve(file);
      }, "image/jpeg");
    });
  };

  const handleCrop = async () => {
    if (completedCrop && imgRef.current) {
      const croppedImageFile = await getCroppedImg(imgRef.current, completedCrop);
      onCrop(croppedImageFile);
    }
    onClose();
  };

  const handleSkip = () => {
    onSkip();
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>Crop Your Image</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              minWidth={50}
              minHeight={50}
              keepSelection
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                style={{ maxWidth: "100%", maxHeight: "400px" }}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="secondary" variant="text">
          Cancel
        </Button>
        <Button onClick={handleSkip} color="secondary" variant="outlined">
          Skip
        </Button>
        <Button onClick={handleCrop} variant="contained">
          Crop & Use
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostImageCropperModal;